import {
  OFFLINE_DATA_CHANGED_EVENT,
  refreshLocalProjectionFromServer,
  syncOfflineChanges,
} from "./api";
import {
  offlinePersistence,
  type OfflineMovementOperation,
} from "./offline";
import {
  fetchWithSessionRetry,
  forgetSessionPassword,
  rememberSessionPassword,
  RemoteAuthenticationError,
  revokeTrustedSession,
  verifyRemotePassword,
} from "./session-auth";
import {
  getSecureOfflineAdapter,
  SecureVaultUnlockError,
  type LocalReplica,
  type SecureOfflineAdapter,
  type SerializedVaultEnvelope,
} from "./secure-vault";
import {
  broadcastTrustedDeviceSignal,
  forgetTrustedDeviceMaterial,
  resumeTrustedRestore,
  storeTrustedDeviceMaterial,
  suspendTrustedRestore,
} from "./trusted-device";
import {
  acquireVaultSessionLock,
  releaseVaultSessionLock,
} from "./vault-session-lock";

export type VaultSyncStatus =
  | "idle"
  | "pending"
  | "syncing"
  | "up_to_date"
  | "conflict"
  | "error";

export interface VaultSyncSnapshot {
  status: VaultSyncStatus;
  error: string | null;
}

export interface VaultOpenOptions {
  trustDevice: boolean;
  online?: boolean;
  trustedAlready?: boolean;
}

export interface VaultTrustResult {
  trusted: boolean;
  warning: string | null;
}

export interface VaultOpenResult extends VaultTrustResult {
  sessionGeneration: number;
}

export const VAULT_SESSION_CLOSING_EVENT = "ma-cave:vault-session-closing";

export class VaultConflictAppliedPendingError extends Error {
  constructor() {
    super(
      "Le choix a été envoyé et peut avoir été appliqué, mais son résultat final n’a pas pu être confirmé. Rouvrez la cave avant toute nouvelle modification.",
    );
    this.name = "VaultConflictAppliedPendingError";
  }
}

interface RemoteVault {
  revision: number;
  envelope: SerializedVaultEnvelope | null;
  updated_at?: string;
}

interface RemoteVaultConflict {
  revision: number;
  envelope: SerializedVaultEnvelope;
  localGeneration: number | null;
  localEnvelope: SerializedVaultEnvelope | null;
}

type VaultSyncSubscriber = (snapshot: VaultSyncSnapshot) => void;

const subscribers = new Set<VaultSyncSubscriber>();
let syncSnapshot: VaultSyncSnapshot = { status: "idle", error: null };
let syncPromise: Promise<void> | null = null;
let currentConflict: RemoteVaultConflict | null = null;
let retryAfterCurrentSync = false;
let retryTimer: number | null = null;
let sessionClosing = false;
let sessionLifecycle: Promise<void> = Promise.resolve();
let remoteVaultLifecycle: Promise<void> = Promise.resolve();
let decryptedSessionGeneration = 0;

const MIN_REMOTE_ITERATIONS = 100_000;
const MAX_REMOTE_ITERATIONS = 2_000_000;
const MAX_REMOTE_CIPHERTEXT_CHARACTERS = 8 * 1024 * 1024;
const MAX_SYNC_PASSES = 8;
const SYNC_RETRY_DELAY_MS = 750;
export const FORGET_DEVICE_ERROR_KEY = "ma-cave:forget-device-error";

function runVaultSessionLifecycle<T>(work: () => Promise<T>): Promise<T> {
  const result = sessionLifecycle.then(work, work);
  sessionLifecycle = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function runRemoteVaultLifecycle<T>(work: () => Promise<T>): Promise<T> {
  const result = remoteVaultLifecycle.then(work, work);
  remoteVaultLifecycle = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function markDecryptedSessionOpened(): number {
  decryptedSessionGeneration += 1;
  return decryptedSessionGeneration;
}

function announceSessionClosing(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(VAULT_SESSION_CLOSING_EVENT));
  }
}

function clearScheduledRetry(): void {
  if (retryTimer === null || typeof window === "undefined") return;
  window.clearTimeout(retryTimer);
  retryTimer = null;
}

async function acquireDecryptedSession(): Promise<void> {
  await acquireVaultSessionLock();
  sessionClosing = false;
}

async function waitForSynchronizationQuiescence(): Promise<void> {
  clearScheduledRetry();
  retryAfterCurrentSync = false;
  const activeSynchronization = syncPromise;
  if (activeSynchronization) {
    await activeSynchronization.catch(() => undefined);
  }
  retryAfterCurrentSync = false;
  clearScheduledRetry();
}

async function quiesceAndLockAdapter(
  adapter: SecureOfflineAdapter | null,
): Promise<void> {
  sessionClosing = true;
  await waitForSynchronizationQuiescence();
  await adapter?.lock();
}

async function releaseSessionIfLocked(
  adapter: SecureOfflineAdapter | null,
): Promise<void> {
  if (adapter?.isUnlocked()) return;
  await releaseVaultSessionLock();
  sessionClosing = false;
}

async function abandonFailedOpening(
  adapter: SecureOfflineAdapter,
): Promise<void> {
  try {
    await quiesceAndLockAdapter(adapter);
  } finally {
    if (!adapter.isUnlocked()) {
      offlinePersistence.resetDecryptedState();
      forgetSessionPassword();
      await releaseSessionIfLocked(adapter);
    }
  }
}

function scheduleSynchronizationRetry(): void {
  if (
    retryTimer !== null ||
    typeof window === "undefined" ||
    (typeof navigator !== "undefined" && navigator.onLine === false)
  ) {
    return;
  }
  retryTimer = window.setTimeout(async () => {
    retryTimer = null;
    await synchronizeSecureReplica();
  }, SYNC_RETRY_DELAY_MS);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isRemoteEnvelope(value: unknown): value is SerializedVaultEnvelope {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value).sort();
  if (
    keys.length !== 5 ||
    keys[0] !== "ciphertext" ||
    keys[1] !== "iterations" ||
    keys[2] !== "iv" ||
    keys[3] !== "salt" ||
    keys[4] !== "version"
  ) {
    return false;
  }
  return (
    value.version === 1 &&
    typeof value.iterations === "number" &&
    Number.isSafeInteger(value.iterations) &&
    value.iterations >= MIN_REMOTE_ITERATIONS &&
    value.iterations <= MAX_REMOTE_ITERATIONS &&
    typeof value.salt === "string" &&
    value.salt.length >= 24 &&
    value.salt.length <= 88 &&
    typeof value.iv === "string" &&
    value.iv.length === 16 &&
    typeof value.ciphertext === "string" &&
    value.ciphertext.length >= 24 &&
    value.ciphertext.length <= MAX_REMOTE_CIPHERTEXT_CHARACTERS
  );
}

function remoteVault(value: unknown): RemoteVault {
  if (
    !isRecord(value) ||
    typeof value.revision !== "number" ||
    !Number.isSafeInteger(value.revision)
  ) {
    throw new Error("Le serveur a renvoyé un coffre invalide.");
  }
  const revision = Number(value.revision);
  if (
    revision < 0 ||
    (revision === 0 && value.envelope !== null) ||
    (revision > 0 && !isRemoteEnvelope(value.envelope))
  ) {
    throw new Error("Le serveur a renvoyé un coffre invalide.");
  }
  if (
    value.updated_at !== undefined &&
    (typeof value.updated_at !== "string" || value.updated_at.length > 64)
  ) {
    throw new Error("Le serveur a renvoyé un coffre invalide.");
  }
  return {
    revision,
    envelope: revision === 0
      ? null
      : value.envelope as SerializedVaultEnvelope,
    ...(typeof value.updated_at === "string" ? { updated_at: value.updated_at } : {}),
  };
}

function messageFromResponse(response: Response): Promise<string> {
  return response
    .clone()
    .json()
    .then((body: { erreur?: { message?: string } }) =>
      body.erreur?.message ?? "Synchronisation distante impossible.",
    )
    .catch(() => "Synchronisation distante impossible.");
}

function publish(next: VaultSyncSnapshot): void {
  syncSnapshot = next;
  for (const subscriber of subscribers) subscriber({ ...next });
}

export function subscribeVaultSync(subscriber: VaultSyncSubscriber): () => void {
  subscribers.add(subscriber);
  subscriber({ ...syncSnapshot });
  return () => subscribers.delete(subscriber);
}

export function getVaultSyncSnapshot(): VaultSyncSnapshot {
  return { ...syncSnapshot };
}

export function hasPendingVaultConflict(): boolean {
  return currentConflict !== null;
}

export function rememberVaultPassword(password: string): void {
  rememberSessionPassword(password);
}

export async function authenticateVaultRemotely(
  password: string,
  trustDevice = false,
): Promise<void> {
  await verifyRemotePassword(password, trustDevice);
  if (trustDevice) forgetSessionPassword();
  else rememberSessionPassword(password);
}

async function readRemoteVault(): Promise<RemoteVault> {
  let response: Response;
  try {
    response = await fetchWithSessionRetry("/api/coffre", {
      method: "GET",
      headers: { Accept: "application/json" },
    });
  } catch (error) {
    if (error instanceof RemoteAuthenticationError) throw error;
    throw new RemoteAuthenticationError(
      error instanceof Error
        ? error.message
        : "Connexion au serveur impossible.",
      0,
    );
  }
  if (!response.ok) {
    const message = await messageFromResponse(response);
    throw new RemoteAuthenticationError(message, response.status);
  }
  return remoteVault(await response.json());
}

async function writeRemoteVault(
  envelope: SerializedVaultEnvelope,
  baseRevision: number,
): Promise<RemoteVault> {
  const response = await fetchWithSessionRetry("/api/coffre", {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ base_revision: baseRevision, envelope }),
  });
  if (response.status === 409) {
    const body = (await response.json()) as {
      erreur?: {
        message?: string;
        details?: Partial<RemoteVault>;
      };
    };
    const details = body.erreur?.details;
    if (details) {
      try {
        const conflict = remoteVault(details);
        if (conflict.envelope && conflict.revision > 0) {
          rememberVaultConflict(
            conflict.revision,
            conflict.envelope,
            null,
          );
        }
      } catch {
        // Une réponse de conflit mal formée ne doit jamais devenir une version
        // sélectionnable. La relecture réseau tentera de lever l'ambiguïté.
      }
    }
    throw new Error(body.erreur?.message ?? "Le coffre a changé sur un autre appareil.");
  }
  if (!response.ok) throw new Error(await messageFromResponse(response));
  return remoteVault(await response.json());
}

function sameEnvelope(
  left: SerializedVaultEnvelope,
  right: SerializedVaultEnvelope,
): boolean {
  return (
    left.version === right.version &&
    left.iterations === right.iterations &&
    left.salt === right.salt &&
    left.iv === right.iv &&
    left.ciphertext === right.ciphertext
  );
}

function rememberVaultConflict(
  revision: number,
  envelope: SerializedVaultEnvelope,
  local: LocalReplica | null,
): RemoteVaultConflict {
  const conflict = {
    revision,
    envelope,
    localGeneration: local?.generation ?? null,
    localEnvelope: local?.envelope ?? null,
  };
  currentConflict = conflict;
  return conflict;
}

function samePresentedConflict(
  conflict: RemoteVaultConflict,
  local: LocalReplica,
  remote: RemoteVault,
): boolean {
  return (
    conflict.localGeneration !== null &&
    conflict.localEnvelope !== null &&
    local.generation === conflict.localGeneration &&
    sameEnvelope(local.envelope, conflict.localEnvelope) &&
    remote.revision === conflict.revision &&
    remote.envelope !== null &&
    sameEnvelope(remote.envelope, conflict.envelope)
  );
}

class VaultConflictChangedError extends Error {
  constructor() {
    super(
      "Le conflit a changé depuis son affichage. Les copies ont été relues ; confirmez à nouveau votre choix.",
    );
    this.name = "VaultConflictChangedError";
  }
}

function refreshPresentedConflict(
  local: LocalReplica,
  remote: RemoteVault,
): void {
  if (remote.envelope && remote.revision > 0) {
    rememberVaultConflict(remote.revision, remote.envelope, local);
    publish({
      status: "conflict",
      error:
        "Le conflit a changé depuis son affichage. Vérifiez les copies puis confirmez à nouveau votre choix.",
    });
  } else {
    currentConflict = null;
    publish({
      status: "error",
      error: "Le coffre distant présenté n’existe plus.",
    });
  }
}

type PushResult =
  | { state: "confirmed"; operations: OfflineMovementOperation[] }
  | { state: "advanced"; baseRevision: number }
  | { state: "pending" }
  | { state: "conflict"; error: Error };
type StablePushResult = Exclude<PushResult, { state: "advanced" }>;

async function reconcileObservedRemote(
  adapter: SecureOfflineAdapter,
  envelope: SerializedVaultEnvelope,
  baseRevision: number,
  observed: RemoteVault,
  conflictError: Error,
): Promise<PushResult | null> {
  if (observed.envelope && sameEnvelope(envelope, observed.envelope)) {
    const operations =
      await adapter.markRemoteConfirmedAndCaptureMutations(
      observed.revision,
      envelope,
    );
    return operations
      ? { state: "confirmed", operations }
      : { state: "advanced", baseRevision: observed.revision };
  }
  if (observed.envelope && observed.revision > baseRevision) {
    rememberVaultConflict(
      observed.revision,
      observed.envelope,
      await adapter.exportReplica(),
    );
    return { state: "conflict", error: conflictError };
  }
  return null;
}

async function pushEnvelope(
  adapter: SecureOfflineAdapter,
  envelope: SerializedVaultEnvelope,
  baseRevision: number,
): Promise<PushResult> {
  try {
    const written = await writeRemoteVault(envelope, baseRevision);
    if (!written.envelope || !sameEnvelope(envelope, written.envelope)) {
      throw new Error(
        "Le serveur n’a pas confirmé exactement le coffre chiffré envoyé.",
      );
    }
    const operations =
      await adapter.markRemoteConfirmedAndCaptureMutations(
      written.revision,
      envelope,
    );
    return operations
      ? { state: "confirmed", operations }
      : { state: "advanced", baseRevision: written.revision };
  } catch (caught) {
    const error = caught instanceof Error
      ? caught
      : new Error("Synchronisation distante impossible.");
    const reportedConflict = currentConflict;
    if (reportedConflict) {
      const reconciled = await reconcileObservedRemote(
        adapter,
        envelope,
        baseRevision,
        { ...reportedConflict },
        error,
      );
      if (reconciled) return reconciled;
    }

    // Le serveur peut avoir validé le PUT alors que sa réponse s'est perdue.
    // Une relecture exacte est la seule preuve autorisant le passage à clean.
    let observed: RemoteVault;
    try {
      observed = await readRemoteVault();
    } catch {
      // Conserver l'erreur initiale : la copie locale reste dirty et aucune
      // panne de la relecture ne doit être assimilée à une confirmation.
      throw error;
    }
    const reconciled = await reconcileObservedRemote(
      adapter,
      envelope,
      baseRevision,
      observed,
      error,
    );
    if (reconciled) return reconciled;
    throw error;
  }
}

async function pushDirtyReplica(
  adapter: SecureOfflineAdapter,
  initialRemote?: RemoteVault,
): Promise<StablePushResult> {
  let observed = initialRemote;
  let confirmedBaseRevision: number | null = null;

  for (let pass = 0; pass < MAX_SYNC_PASSES; pass += 1) {
    const local = await adapter.exportReplica();
    if (!local) throw new Error("La copie locale chiffrée est indisponible.");
    const metadata = await adapter.getSyncMetadata();
    if (!metadata.dirty) {
      const operations =
        await adapter.markRemoteConfirmedAndCaptureMutations(
          metadata.baseRevision,
          local.envelope,
        );
      if (operations) {
        return { state: "confirmed", operations };
      }
      continue;
    }
    const baseRevision = Math.max(
      metadata.baseRevision,
      confirmedBaseRevision ?? 0,
    );

    if (observed) {
      if (observed.revision < baseRevision) {
        throw new Error("La révision distante est antérieure à la copie locale.");
      }
      const reconciled = await reconcileObservedRemote(
        adapter,
        local.envelope,
        baseRevision,
        observed,
        new Error("Le coffre a changé sur un autre appareil."),
      );
      observed = undefined;
      if (reconciled?.state === "advanced") {
        confirmedBaseRevision = reconciled.baseRevision;
        continue;
      }
      if (reconciled) return reconciled;
    }

    const pushed = await pushEnvelope(adapter, local.envelope, baseRevision);
    if (pushed.state === "advanced") {
      confirmedBaseRevision = pushed.baseRevision;
      continue;
    }
    return pushed;
  }

  // Des écritures locales continues peuvent empêcher de confirmer exactement
  // une génération. On conserve dirty et on laisse un prochain signal relancer
  // la boucle, sans annoncer à tort que tout est à jour.
  return { state: "pending" };
}

async function persistTrustedMaterial(
  adapter: SecureOfflineAdapter,
  password: string,
): Promise<VaultTrustResult> {
  try {
    await storeTrustedDeviceMaterial(adapter.getKeyMaterial());
    forgetSessionPassword();
    return { trusted: true, warning: null };
  } catch {
    await forgetTrustedDeviceMaterial().catch(() => undefined);
    try {
      await verifyRemotePassword(password, false);
      rememberSessionPassword(password);
    } catch {
      // Le mot de passe reste uniquement en mémoire pour que la session
      // courante puisse continuer hors ligne si le serveur est injoignable.
      rememberSessionPassword(password);
    }
    return {
      trusted: false,
      warning:
        "Ce navigateur n’a pas pu conserver la clé de confiance. Ma Cave reste ouverte, mais le mot de passe sera redemandé.",
    };
  }
}

async function completeVaultTrust(
  password: string,
  options: VaultOpenOptions,
  remoteAlreadyVerified = false,
): Promise<VaultTrustResult> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter || !adapter.isUnlocked()) {
    throw new Error("Le coffre doit être ouvert avant d’enregistrer la confiance.");
  }
  const online = options.online ?? navigator.onLine !== false;
  if (options.trustDevice) {
    if (!online && !options.trustedAlready) {
      rememberSessionPassword(password);
      return {
        trusted: false,
        warning:
          "Une connexion Internet est nécessaire pour approuver un nouvel appareil. Le mot de passe sera redemandé.",
      };
    }
    if (!remoteAlreadyVerified && online) {
      try {
        await authenticateVaultRemotely(password, true);
      } catch (error) {
        if (!(error instanceof RemoteAuthenticationError) || error.status !== 0) {
          throw error;
        }
        if (!options.trustedAlready) {
          rememberSessionPassword(password);
          return {
            trusted: false,
            warning:
              "Le serveur est injoignable : ce nouvel appareil n’a pas été approuvé. Le mot de passe sera redemandé.",
          };
        }
        // Une panne de transport ne révoque pas une confiance déjà établie.
        // Réécrire le matériau lève aussi un verrouillage explicite antérieur.
        return persistTrustedMaterial(adapter, password);
      }
    }
    return persistTrustedMaterial(adapter, password);
  }

  await forgetTrustedDeviceMaterial().catch(() => undefined);
  resumeTrustedRestore();
  if (online && !remoteAlreadyVerified) {
    try {
      await authenticateVaultRemotely(password, false);
    } catch (error) {
      if (!(error instanceof RemoteAuthenticationError) || error.status !== 0) {
        throw error;
      }
      rememberSessionPassword(password);
    }
  } else {
    rememberSessionPassword(password);
    if (!online) void revokeTrustedSession();
  }
  return { trusted: false, warning: null };
}

async function unlockSecureReplicaNow(
  password: string,
  options: VaultOpenOptions,
): Promise<VaultOpenResult> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) {
    throw new Error("Le stockage chiffré n’est pas disponible sur cet appareil.");
  }
  await acquireDecryptedSession();
  try {
    await adapter.unlock(password);
    await offlinePersistence.ready();
    const result = await completeVaultTrust(password, options);
    if ((options.online ?? navigator.onLine !== false) && navigator.onLine !== false) {
      void synchronizeSecureReplica();
    }
    return { ...result, sessionGeneration: markDecryptedSessionOpened() };
  } catch (error) {
    await abandonFailedOpening(adapter).catch(() => undefined);
    throw error;
  }
}

export function unlockSecureReplica(
  password: string,
  options: VaultOpenOptions,
): Promise<VaultOpenResult> {
  return runVaultSessionLifecycle(() => unlockSecureReplicaNow(password, options));
}

async function restoreTrustedReplicaNow(
  material: CryptoKey,
  online = navigator.onLine !== false,
): Promise<number> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) {
    throw new Error("Le stockage chiffré n’est pas disponible sur cet appareil.");
  }
  await acquireDecryptedSession();
  try {
    const hasLocalReplica = await adapter.hasReplica();
    const hasLinkedReplica = hasLocalReplica
      ? await adapter.hasLinkedReplica()
      : false;
    if (hasLocalReplica && !hasLinkedReplica) {
      throw new Error(
        "La copie locale n’est pas encore liée. Saisissez le mot de passe pour terminer son activation.",
      );
    }

    let remote: RemoteVault | null = null;
    let remoteAvailable = online;
    if (online) {
      try {
        remote = await readRemoteVault();
      } catch (error) {
        if (
          !hasLinkedReplica ||
          !(error instanceof RemoteAuthenticationError) ||
          error.status !== 0
        ) {
          throw error;
        }
        // `navigator.onLine` est seulement un indice. Une erreur de transport
        // réelle autorise la copie locale liée, contrairement à un 401.
        remoteAvailable = false;
      }
    }
    if (hasLocalReplica) {
      await adapter.unlock(material);
      if (remote && remote.revision === 0) {
        throw new Error(
          "La session distante ne correspond plus à cette copie. Saisissez le mot de passe pour continuer.",
        );
      }
    } else {
      if (!online || !remote?.envelope || remote.revision < 1) {
        throw new Error(
          "Une connexion Internet et une copie distante valide sont nécessaires pour restaurer cet appareil.",
        );
      }
      await adapter.adoptReplica(remote.envelope, remote.revision, material);
    }
    await offlinePersistence.ready();
    forgetSessionPassword();
    resumeTrustedRestore();
    if (remoteAvailable) void synchronizeSecureReplica();
    return markDecryptedSessionOpened();
  } catch (error) {
    if (
      (error instanceof RemoteAuthenticationError && error.status === 401) ||
      error instanceof SecureVaultUnlockError
    ) {
      // La lecture de VaultGate reste pure. Le nettoyage d’une confiance
      // effectivement invalide se fait ici, pendant que ce flux possède encore
      // le Web Lock exclusif.
      await forgetTrustedDeviceMaterial().catch(() => undefined);
    }
    await abandonFailedOpening(adapter).catch(() => undefined);
    throw error;
  }
}

export function restoreTrustedReplica(
  material: CryptoKey,
  online = navigator.onLine !== false,
): Promise<number> {
  return runVaultSessionLifecycle(() =>
    restoreTrustedReplicaNow(material, online),
  );
}

async function activateSecureReplicaNow(
  password: string,
  options: VaultOpenOptions = { trustDevice: false },
): Promise<VaultOpenResult> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) throw new Error("Le stockage chiffré n’est pas disponible sur cet appareil.");

  await acquireDecryptedSession();
  try {
    await authenticateVaultRemotely(password, options.trustDevice);
    const remote = await readRemoteVault();
    currentConflict = null;
    publish({ status: "syncing", error: null });
    const hasLocalReplica = await adapter.hasReplica();
    if (remote.envelope && remote.revision > 0) {
      let local = null;
      if (hasLocalReplica) {
        await adapter.unlock(password);
        local = await adapter.exportReplica();
      } else {
        local = await adapter.migrateLegacyReplica(password);
      }
      if (local) {
        if (!sameEnvelope(local.envelope, remote.envelope)) {
          rememberVaultConflict(remote.revision, remote.envelope, local);
          const error = new Error(
            "Une copie locale inachevée et un coffre distant différent existent. Choisissez explicitement la version à conserver.",
          );
          publish({ status: "conflict", error: error.message });
          throw error;
        }
        if (!(await adapter.markRemoteConfirmed(remote.revision, local.envelope))) {
          throw new Error(
            "La copie locale a changé pendant sa confirmation distante.",
          );
        }
      } else {
        await adapter.adoptReplica(remote.envelope, remote.revision, password);
      }
      await offlinePersistence.ready();
      publish({ status: "up_to_date", error: null });
      const result = await completeVaultTrust(password, options, true);
      return { ...result, sessionGeneration: markDecryptedSessionOpened() };
    }

    if (hasLocalReplica) {
      await adapter.unlock(password);
    } else {
      await adapter.activate(password, { baseRevision: 0, dirty: true });
    }
    await offlinePersistence.ready();
    await refreshLocalProjectionFromServer();
    const replica = await adapter.exportReplica();
    if (!replica) throw new Error("La copie locale n’a pas pu être préparée.");
    const pushed = await pushDirtyReplica(adapter, remote);
    if (pushed.state === "conflict") throw pushed.error;
    if (pushed.state !== "confirmed") {
      throw new Error("Le serveur n’a pas confirmé l’activation hors ligne.");
    }
    publish({ status: "up_to_date", error: null });
    const result = await completeVaultTrust(password, options, true);
    return { ...result, sessionGeneration: markDecryptedSessionOpened() };
  } catch (error) {
    // La réplique staging reste chiffrée et non liée. Une panne ne doit jamais
    // supprimer automatiquement une outbox migrée ou une activation à reprendre.
    if (options.trustDevice) {
      await forgetTrustedDeviceMaterial().catch(() => undefined);
      await verifyRemotePassword(password, false).catch(() => undefined);
    }
    await abandonFailedOpening(adapter).catch(() => undefined);
    throw error;
  }
}

export function activateSecureReplica(
  password: string,
  options: VaultOpenOptions = { trustDevice: false },
): Promise<VaultOpenResult> {
  return runVaultSessionLifecycle(() =>
    activateSecureReplicaNow(password, options),
  );
}

async function runSecureSynchronization(): Promise<void> {
  // Une passe peut avoir été mise en file avant le début d’une fermeture.
  // Revalider une fois le mutex réseau acquis évite tout GET/PUT post-release.
  if (sessionClosing) return;
  const adapter = getSecureOfflineAdapter();
  if (!adapter) {
    // Sans coffre chiffré il n’existe aucune enveloppe CAS pouvant autoriser
    // un effet D1. Le signal reste donc sans effet plutôt que de contourner
    // l’invariant de causalité.
    return;
  }
  if (!adapter.isUnlocked() || !(await adapter.hasLinkedReplica())) return;

  publish({ status: "syncing", error: null });
  currentConflict = null;

  let local = await adapter.exportReplica();
  if (!local) throw new Error("La copie locale chiffrée est indisponible.");
  let metadata = await adapter.getSyncMetadata();
  const remote = await readRemoteVault();
  let confirmedOperations: OfflineMovementOperation[] | null = null;

  if (metadata.dirty) {
    // L'enveloppe contenant l'outbox est réconciliée avant toute mutation D1.
    // Un conflit s'arrête donc ici sans effet métier distant irréversible.
    const initialPush = await pushDirtyReplica(adapter, remote);
    if (initialPush.state === "conflict") {
      publish({ status: "conflict", error: initialPush.error.message });
      return;
    }
    if (initialPush.state === "pending") {
      publish({ status: "pending", error: null });
      retryAfterCurrentSync = true;
      return;
    }
    confirmedOperations = initialPush.operations;
  } else {
    if (remote.revision < metadata.baseRevision) {
      throw new Error("La révision distante est antérieure à la copie locale.");
    }
    if (
      remote.envelope &&
      remote.revision === metadata.baseRevision &&
      !sameEnvelope(local.envelope, remote.envelope)
    ) {
      rememberVaultConflict(remote.revision, remote.envelope, local);
      publish({
        status: "conflict",
        error: "Deux coffres différents portent la même révision.",
      });
      return;
    }
    if (remote.envelope && remote.revision > metadata.baseRevision) {
      try {
        await offlinePersistence.replaceStoredSnapshot(async () => {
          await adapter.replaceFromRemote(remote.envelope!, remote.revision);
        });
      } catch (error) {
        const latest = await adapter.getSyncMetadata().catch(() => null);
        if (latest?.dirty) {
          rememberVaultConflict(
            remote.revision,
            remote.envelope,
            await adapter.exportReplica(),
          );
          publish({
            status: "conflict",
            error: "La copie locale a changé pendant la récupération distante.",
          });
          return;
        }
        throw error;
      }
      window.dispatchEvent(new Event(OFFLINE_DATA_CHANGED_EVENT));
    }
  }

  if (confirmedOperations === null) {
    // Le coffre peut être déjà clean tout en contenant une outbox confirmée
    // lors d’une passe précédente. Cette capture atomique la rend éligible,
    // et pousse d’abord toute écriture concurrente déjà visible ici.
    const flushCandidate = await pushDirtyReplica(adapter);
    if (flushCandidate.state === "conflict") {
      publish({ status: "conflict", error: flushCandidate.error.message });
      return;
    }
    if (flushCandidate.state === "pending") {
      publish({ status: "pending", error: null });
      retryAfterCurrentSync = true;
      return;
    }
    confirmedOperations = flushCandidate.operations;
  }

  // Seules les actions présentes dans l’enveloppe exactement confirmée peuvent
  // toucher D1. Un clic postérieur reste optimiste et attend la passe suivante.
  const flush = await syncOfflineChanges(confirmedOperations);

  // Une seule projection D1 remplace atomiquement le modèle de lecture local,
  // détails compris, puis y réapplique l’outbox avant tout PUT du coffre.
  await refreshLocalProjectionFromServer();
  local = await adapter.exportReplica();
  if (!local) throw new Error("La copie locale chiffrée est indisponible.");
  metadata = await adapter.getSyncMetadata();

  if (metadata.dirty) {
    const pushed = await pushDirtyReplica(adapter);
    if (pushed.state === "conflict") {
      publish({ status: "conflict", error: pushed.error.message });
      return;
    }
    if (pushed.state === "pending") {
      publish({ status: "pending", error: null });
      retryAfterCurrentSync = true;
      return;
    }
  }

  if ((await adapter.getSyncMetadata()).dirty) {
    publish({ status: "pending", error: null });
    retryAfterCurrentSync = true;
    return;
  }
  if (flush.pending_count > 0 && flush.status !== "error") {
    publish({ status: "pending", error: null });
    retryAfterCurrentSync = true;
    return;
  }
  publish({ status: "up_to_date", error: null });
}

export async function synchronizeSecureReplica(): Promise<void> {
  if (sessionClosing) return;
  if (syncPromise) {
    // Un clic peut ajouter une action après que la passe courante a observé
    // l’outbox vide. Mémoriser le signal garantit une nouvelle passe après le
    // quiescement, au lieu de simplement rejoindre une Promise presque terminée.
    retryAfterCurrentSync = true;
    return syncPromise;
  }
  clearScheduledRetry();
  retryAfterCurrentSync = false;
  syncPromise = runRemoteVaultLifecycle(runSecureSynchronization)
    .catch((error) => {
      publish({
        status: "error",
        error: error instanceof Error ? error.message : "Synchronisation impossible.",
      });
    })
    .finally(() => {
      const shouldRetry = retryAfterCurrentSync;
      retryAfterCurrentSync = false;
      syncPromise = null;
      if (shouldRetry) scheduleSynchronizationRetry();
    });
  return syncPromise;
}

async function resolveVaultConflictNow(
  choice: "local" | "remote",
): Promise<boolean> {
  const adapter = getSecureOfflineAdapter();
  const conflict = currentConflict;
  if (!adapter || !conflict || !adapter.isUnlocked()) return false;

  let effectMayHaveStarted = false;
  const resolved = await runRemoteVaultLifecycle(async () => {
  if (
    sessionClosing ||
    !adapter.isUnlocked() ||
    currentConflict !== conflict
  ) {
    return false;
  }
  try {
    const local = await adapter.exportReplica();
    if (!local) throw new Error("La copie locale chiffrée est indisponible.");
    const remote = await readRemoteVault();
    if (!samePresentedConflict(conflict, local, remote)) {
      refreshPresentedConflict(local, remote);
      return false;
    }

    publish({ status: "syncing", error: null });
    if (choice === "remote") {
      await offlinePersistence.replaceStoredSnapshot(async () => {
        if (sessionClosing || !adapter.isUnlocked()) {
          throw new Error("Le coffre a été verrouillé avant la résolution.");
        }
        // Une action locale peut avoir été créée pendant la première
        // relecture. Revalider les deux identités sous la courte barrière
        // cache avant l’unique remplacement qui pourrait l’effacer.
        const latestLocal = await adapter.exportReplica();
        if (!latestLocal) {
          throw new Error("La copie locale chiffrée est indisponible.");
        }
        const latestRemote = await readRemoteVault();
        if (!samePresentedConflict(conflict, latestLocal, latestRemote)) {
          refreshPresentedConflict(latestLocal, latestRemote);
          throw new VaultConflictChangedError();
        }
        // À partir de cet appel, le remplacement local peut avoir été publié
        // même si le rechargement qui suit échoue.
        effectMayHaveStarted = true;
        await adapter.resolveConflictWithRemote(
          conflict.envelope,
          conflict.revision,
        );
      });
      window.dispatchEvent(new Event(OFFLINE_DATA_CHANGED_EVENT));
    } else {
      if (sessionClosing || !adapter.isUnlocked()) {
        throw new Error("Le coffre a été verrouillé avant la résolution.");
      }
      // Après l’envoi du PUT, une réponse perdue ne permet plus d’affirmer
      // qu’aucun effet distant n’a eu lieu.
      effectMayHaveStarted = true;
      const pushed = await pushEnvelope(
        adapter,
        local.envelope,
        conflict.revision,
      );
      if (pushed.state !== "confirmed") {
        throw new VaultConflictAppliedPendingError();
      }
      await offlinePersistence.reloadFromStorage();
    }
    currentConflict = null;
    publish({ status: "pending", error: null });
    return true;
  } catch (error) {
    if (error instanceof VaultConflictChangedError && !effectMayHaveStarted) {
      return false;
    }
    if (effectMayHaveStarted) {
      const outcomeError = error instanceof VaultConflictAppliedPendingError
        ? error
        : new VaultConflictAppliedPendingError();
      publish({ status: "error", error: outcomeError.message });
      throw outcomeError;
    }
    publish({
      status: "error",
      error: error instanceof Error ? error.message : "Résolution impossible.",
    });
    return false;
  }
  });
  if (!resolved) return false;

  // Ne jamais rendre l’application sur la copie choisie avant une passe
  // complète de validation, projection et réconciliation du coffre.
  await synchronizeSecureReplica();
  if (syncSnapshot.status !== "up_to_date") {
    // Le choix a déjà eu un effet local ou distant. Ne jamais le confondre
    // avec les retours false ci-dessus, qui sont strictement pré-effet.
    const error = new VaultConflictAppliedPendingError();
    publish({ status: "error", error: error.message });
    throw error;
  }
  return true;
}

export function resolveVaultConflict(
  choice: "local" | "remote",
): Promise<boolean> {
  return runVaultSessionLifecycle(() => resolveVaultConflictNow(choice));
}

async function prepareVaultConflictResolutionNow(
  password: string,
  trustDevice = false,
): Promise<number> {
  const adapter = getSecureOfflineAdapter();
  const presented = currentConflict;
  if (!adapter || !presented) {
    throw new Error("Aucun conflit de coffre n’est prêt à être résolu.");
  }
  await acquireDecryptedSession();
  try {
    await authenticateVaultRemotely(password, trustDevice);
    await adapter.unlock(password);
    const local = await adapter.exportReplica();
    if (!local) throw new Error("La copie locale chiffrée est indisponible.");
    const remote = await readRemoteVault();
    if (!samePresentedConflict(presented, local, remote)) {
      refreshPresentedConflict(local, remote);
      throw new VaultConflictChangedError();
    }
    return markDecryptedSessionOpened();
  } catch (error) {
    await abandonFailedOpening(adapter).catch(() => undefined);
    throw error;
  }
}

export function resolveVaultConflictAndOpen(
  choice: "local" | "remote",
  password: string,
  options: VaultOpenOptions,
): Promise<VaultOpenResult | null> {
  return runVaultSessionLifecycle(async () => {
    let sessionGeneration: number | null = null;
    try {
      sessionGeneration = await prepareVaultConflictResolutionNow(
        password,
        options.trustDevice,
      );
      if (!(await resolveVaultConflictNow(choice))) {
        // Le Gate est déjà fermé pendant une résolution d’activation. Ne pas
        // émettre closing ici : il doit pouvoir représenter le conflit frais.
        await discardUnlockedVaultSessionNow(sessionGeneration, false);
        return null;
      }
      const trust = await completeVaultTrust(password, options, true);
      return { ...trust, sessionGeneration };
    } catch (error) {
      if (sessionGeneration !== null) {
        await discardUnlockedVaultSessionNow(sessionGeneration, false).catch(
          () => undefined,
        );
      }
      throw error;
    }
  });
}

async function lockSecureReplicaNow(
  reloadPage: () => void = () => window.location.reload(),
): Promise<void> {
  // Un callback de timer peut devenir stale après une libération. Il doit
  // reprendre la lease avant toute mutation partagée ou tout broadcast.
  await acquireDecryptedSession();
  announceSessionClosing();
  suspendTrustedRestore();
  const adapter = getSecureOfflineAdapter();
  try {
    await quiesceAndLockAdapter(adapter);
  } finally {
    if (!adapter?.isUnlocked()) offlinePersistence.resetDecryptedState();
    forgetSessionPassword();
    await releaseSessionIfLocked(adapter);
    broadcastTrustedDeviceSignal("locked");
    reloadPage();
  }
}

export function lockSecureReplica(reloadPage?: () => void): Promise<void> {
  return runVaultSessionLifecycle(() => lockSecureReplicaNow(reloadPage));
}

async function discardUnlockedVaultSessionNow(
  expectedGeneration?: number,
  announce = true,
): Promise<boolean> {
  if (
    expectedGeneration !== undefined &&
    expectedGeneration !== decryptedSessionGeneration
  ) {
    return false;
  }
  const adapter = getSecureOfflineAdapter();
  await acquireDecryptedSession();
  if (announce) announceSessionClosing();
  const wasUnlocked = adapter?.isUnlocked() ?? false;
  try {
    await quiesceAndLockAdapter(adapter);
  } finally {
    if (wasUnlocked && !adapter?.isUnlocked()) {
      offlinePersistence.resetDecryptedState();
    }
    forgetSessionPassword();
    await releaseSessionIfLocked(adapter);
  }
  return true;
}

export function discardUnlockedVaultSession(
  expectedGeneration?: number,
): Promise<boolean> {
  return runVaultSessionLifecycle(() =>
    discardUnlockedVaultSessionNow(expectedGeneration),
  );
}

async function invalidateTrustedDeviceSessionNow(): Promise<void> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) return;
  await acquireDecryptedSession();
  announceSessionClosing();
  try {
    await quiesceAndLockAdapter(adapter);
    await forgetTrustedDeviceMaterial();
    suspendTrustedRestore();
    offlinePersistence.resetDecryptedState();
    forgetSessionPassword();
    broadcastTrustedDeviceSignal("locked");
  } finally {
    await releaseSessionIfLocked(adapter);
  }
}

export function invalidateTrustedDeviceSession(): Promise<void> {
  return runVaultSessionLifecycle(invalidateTrustedDeviceSessionNow);
}

async function forgetSecureDeviceNow(
  reloadPage: () => void = () => window.location.reload(),
): Promise<boolean> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) return false;
  let forgotten = false;
  let sessionAcquired = false;
  try {
    await acquireDecryptedSession();
    sessionAcquired = true;
    announceSessionClosing();
    await quiesceAndLockAdapter(adapter);
    await forgetTrustedDeviceMaterial();
    forgotten = true;
    offlinePersistence.resetDecryptedState();
    forgetSessionPassword();
    broadcastTrustedDeviceSignal("forgotten");
    // Le fetch `keepalive` tente la révocation après le rechargement sans
    // maintenir les données claires affichées si le réseau ne répond pas.
    void revokeTrustedSession();
    try {
      sessionStorage.removeItem(FORGET_DEVICE_ERROR_KEY);
    } catch {
      // Le stockage de message n'est pas requis pour l'effacement.
    }
  } catch (error) {
    if (sessionAcquired) {
      suspendTrustedRestore();
      offlinePersistence.resetDecryptedState();
      forgetSessionPassword();
      broadcastTrustedDeviceSignal("locked");
    }
    try {
      sessionStorage.setItem(
        FORGET_DEVICE_ERROR_KEY,
        error instanceof Error
          ? `La confiance locale n’a pas pu être retirée : ${error.message}`
          : "La confiance locale n’a pas pu être retirée.",
      );
    } catch {
      // La réplique restera visible au redémarrage, preuve que l'oubli a échoué.
    }
  } finally {
    if (sessionAcquired) await releaseSessionIfLocked(adapter);
    reloadPage();
  }
  return forgotten;
}

export function forgetSecureDevice(
  reloadPage: () => void = () => window.location.reload(),
): Promise<boolean> {
  return runVaultSessionLifecycle(() => forgetSecureDeviceNow(reloadPage));
}

async function discardSecureDeviceReplicaNow(
  expectedState: "unreadable" | "staging",
  reloadPage: () => void = () => window.location.reload(),
): Promise<boolean> {
  const adapter = getSecureOfflineAdapter();
  if (!adapter) return false;
  let discarded = false;
  let sessionAcquired = false;
  try {
    await acquireDecryptedSession();
    sessionAcquired = true;
    announceSessionClosing();
    await quiesceAndLockAdapter(adapter);
    // Relire après l’acquisition et le quiescement : l’onglet qui supprime ne
    // se fie jamais à l’état observé avant d’obtenir l’exclusivité navigateur.
    let hasReadableReplica = false;
    let unreadable = false;
    try {
      hasReadableReplica = await adapter.hasReplica();
    } catch {
      unreadable = true;
    }
    if (expectedState === "unreadable") {
      if (!unreadable) {
        throw new Error(
          "La copie locale est redevenue lisible. Elle a été conservée ; rechargez Ma Cave avant toute suppression.",
        );
      }
    } else {
      if (unreadable || !hasReadableReplica) {
        throw new Error(
          "La copie locale a changé. Elle a été conservée ; rechargez Ma Cave avant toute suppression.",
        );
      }
      if (await adapter.hasLinkedReplica()) {
        throw new Error(
          "La copie locale a été activée dans un autre onglet. Elle a été conservée ; rechargez Ma Cave.",
        );
      }
    }
    await forgetTrustedDeviceMaterial();
    await adapter.forget();
    discarded = true;
    offlinePersistence.resetDecryptedState();
    forgetSessionPassword();
    broadcastTrustedDeviceSignal("forgotten");
    void revokeTrustedSession();
    try {
      sessionStorage.removeItem(FORGET_DEVICE_ERROR_KEY);
    } catch {
      // Le rechargement reste suffisant après la suppression confirmée.
    }
  } catch (error) {
    if (sessionAcquired) {
      suspendTrustedRestore();
      offlinePersistence.resetDecryptedState();
      forgetSessionPassword();
      broadcastTrustedDeviceSignal("locked");
    }
    try {
      sessionStorage.setItem(
        FORGET_DEVICE_ERROR_KEY,
        error instanceof Error
          ? `La copie locale n’a pas pu être supprimée : ${error.message}`
          : "La copie locale n’a pas pu être supprimée.",
      );
    } catch {
      // La présence de la réplique au redémarrage indique l’échec.
    }
  } finally {
    if (sessionAcquired) await releaseSessionIfLocked(adapter);
    reloadPage();
  }
  return discarded;
}

export function discardSecureDeviceReplica(
  expectedState: "unreadable" | "staging",
  reloadPage: () => void = () => window.location.reload(),
): Promise<boolean> {
  return runVaultSessionLifecycle(() =>
    discardSecureDeviceReplicaNow(expectedState, reloadPage),
  );
}
